import { SanityClient } from '@sanity/client'

import { MultipleChoiceTestData } from '~/types/multiple-choice'
import { AppLanguage } from '~/types/locale'
import { Infos } from '~/types/index'
import { QuestMetadata } from '~/types/quest'
import { studytips, studytipsItem, studytipsItemList } from '~/shared/types/studyTips.types'
import { TopicOverview, TheoryType, TopicGroq, Redirect, AltLanguagePath } from '~/types/theory'
import { CampaignContent } from '~/shared/campaign/Campaign.types'
import {
  theoryTopOverviewGroq,
  theoryAltLanguagePathGroq,
  theoryTopicGroq,
  theoryRedirectTopicGroq,
  modalTheoryGroq,
} from '~/shared/queries/theory'
import { campaignContentGroq } from '~/shared/queries/campaign'
import sanityClient from '~/shared/sanity/sanity-client'

import { DeprecatedSanityLanguageCodes } from './types'

/**
 * Initiate Sanity Client,
 * All config values will be found in Sanity.io
 */
export class Sanity {
  private client: SanityClient

  public constructor() {
    this.client = sanityClient
  }

  /**
   * Query to fetch a Multiple Choice Test from a path.
   * @param {Object} obj - An object.
   * @param {string} obj.url - The path for the Topic entry.
   * @returns A Multiple Choice Test.
   */
  public getMultipleChoiceTest({ url }: { url: string }): Promise<MultipleChoiceTestData> {
    return this.client.fetch(
      `*[_type == "multipleChoiceTest" && url == $url][0]{
        multipleChoiceQuestions[]{
          question,
          options,
        },
        scoreTable[]{
          points,
          score,
        },
        trophy,
        url,
        "taskReference": *[_type == "mappingQuizSubTask" && references(^._id)][0]{
          taskId
        },
      }{
        multipleChoiceQuestions,
        scoreTable,
        trophy,
        url,
        "taskId": taskReference.taskId
      }`,
      {
        url,
      },
    )
  }

  /**
   * Query to fetch the entry urls for the active path.
   * @param {Object} obj - An object.
   * @param {string} obj.drillUrl - The path for the Drill Topic.
   * @returns An array of urls for the active paths' Drill Topic entries.
   */
  public getMultipleChoiceTests({ drillUrl }: { drillUrl: string }): Promise<Array<{ url: string }>> {
    return this.client.fetch(
      `*[_type == "multipleChoiceTest" && url match $drillUrl]{
        url,
      }`,
      {
        drillUrl,
      },
    )
  }

  /**
   * This will return all study tips by language
   * @param param0 language
   * @returns
   */
  public getStudytips({ language }: { language: string }): Promise<studytips[]> {
    return this.client.fetch(
      `*[_type == "studytips" && language == $language]|order(order asc){
        "name": coalesce(name,''),
        "icon": coalesce(icon.asset->url,''),
        "key": coalesce(key, '')
      }`,
      {
        language,
      },
    )
  }

  /**
   * This will return all item of an specific study tips
   * @param param0
   * @returns
   */
  public getStudytipsItemList({ key, language }: { key: string; language: string }): Promise<studytipsItemList | null> {
    return this.client.fetch(
      `*[_type == "studytips" && key == $key && language == $language][0]{
        "name": coalesce(name,''),
        metaDescription,
        "items": items[] | order(itemNumber asc) {
           "name": coalesce(name,''),
           "icon": coalesce(icon.asset->url,''),
           "id": itemNumber
        },
        intro
      }`,
      {
        key,
        language,
      },
    )
  }

  /**
   * To get specific studytips item
   * @param param0
   * @returns
   */
  public getStudytipsItem({
    key,
    language,
    id,
  }: {
    key: string
    language: string
    id: number
  }): Promise<studytipsItem | null> {
    return this.client.fetch(
      `*[_type == "studytips" && key == $key && language == $language][0]{
        name,
        "item": items[itemNumber == $id][0]{
          metaDescription,
          "name": coalesce(name,''),
          "id": itemNumber,
          description
        },
       "maxId": count(items)
      }`,
      {
        key,
        language,
        id,
      },
    )
  }

  /**
   * This function will return users count
   * @returns Users Count
   */
  public getUserCount(): Promise<Infos | null> {
    return this.client.fetch(
      `*[_type == "infos"][0]{
          "userCount": coalesce(userCount,0),
          "tutorCount": coalesce(mentorCount,0),
      }`,
    )
  }

  public getQuestMetadata({ language }: { language: DeprecatedSanityLanguageCodes }): Promise<QuestMetadata[]> {
    return this.client.fetch(
      `*[_type == "quest"]{
        "dialogue": dialogue.${language},
        "dialogueTrophy": dialogueTrophy.${language},
        "panel": panel.${language},
        _id,
        questId,
        shadowTrophy,
        trophy,
        type,
        totalPoints,
        totalStars,
        points,
      }`,
      { language },
    )
  }

  public getEncyclopediaTopic({ language, path }: { language: string; path: string }): Promise<TopicGroq> {
    return this.client.fetch(theoryTopicGroq, {
      language,
      path,
    })
  }

  public getAlternativeLanguagePath({ language, path }: { language: string; path: string }): Promise<AltLanguagePath> {
    return this.client.fetch(theoryAltLanguagePathGroq, {
      language,
      path,
    })
  }

  public getEncyclopediaModalTopics({ htmlKeys, language }: { htmlKeys: string[]; language: string }): Promise<Array<{
    h1: string
    htmlKey: string
    htmlContent: string[]
    cssUrl: string
  }> | null> {
    return this.client.fetch(modalTheoryGroq, {
      htmlKeys,
      language,
    })
  }

  public getEncyclopediaTopOverview({
    language,
    type,
  }: {
    language: string
    type: TheoryType
  }): Promise<{ topicOverview: TopicOverview[] } | null> {
    return this.client.fetch(theoryTopOverviewGroq, {
      language,
      type,
    })
  }

  public getEncyclopediaRedirect({ language, path }: { language: string; path: string }): Promise<Redirect> {
    const paths = path
      .split('/')
      .map((_, index, arr) => arr.slice(0, index + 1).join('/'))
      .filter(Boolean)
    return this.client.fetch(theoryRedirectTopicGroq, {
      language,
      path,
      paths,
    })
  }

  public getCampaignContent({ path }: { path: string }): Promise<CampaignContent> {
    return this.client.fetch(campaignContentGroq, {
      path,
    })
  }
}

export const SANITY = new Sanity()

export function getSanityLanguage(locale: AppLanguage): DeprecatedSanityLanguageCodes {
  return locale === 'no' ? 'nb' : locale
}
